import { CircuitBoard, Info, Plug, PlugZap, RadioTower } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '../../components/ui/badge'
import { Card, CardContent, CardHeader } from '../../components/ui/card'
import { useFormatters } from '../../i18n/format'
import { cn } from '../../lib/cn'
import type { CapabilityProfile, CapabilityReasonCode } from './api-types'
import { canBusVerdict, type CapabilityKnowledge } from './capability-model'

/**
 * Absence, explained (invariant 6).
 *
 * The API answers with a reason CODE and the web catalog turns it into a sentence,
 * so the three cases stay three different support tickets:
 *
 *   no_telemetry_in_window            the tracker is silent — power, SIM, antenna
 *   can_bus_not_reporting             the tracker talks, the bus does not — program
 *                                     number or harness
 *   optional_peripherals_not_reporting  no probe, no reader, no tag — nothing is wrong
 *
 * Collapsing them into "no data" sends an installer to check a working device. The
 * program number travels with the CAN case because it is the single field most
 * likely to be the cause, and the person reading this is the person who can change it.
 */

const REASON_KEY: Record<Exclude<CapabilityReasonCode, null>, string> = {
  can_bus_not_reporting: 'canBusNotReporting',
  optional_peripherals_not_reporting: 'optionalPeripheralsNotReporting',
  no_telemetry_in_window: 'noTelemetryInWindow',
}

const REASON_ICON: Record<Exclude<CapabilityReasonCode, null>, LucideIcon> = {
  can_bus_not_reporting: CircuitBoard,
  optional_peripherals_not_reporting: Plug,
  no_telemetry_in_window: RadioTower,
}

type Tone = 'info' | 'warning' | 'neutral'

const TONES: Record<Tone, string> = {
  info: 'border-info/40 bg-info-subtle/60 text-info',
  warning: 'border-warning/40 bg-warning-subtle/60 text-warning',
  neutral: 'border-border bg-surface-muted text-foreground-subtle',
}

const REASON_TONE: Record<Exclude<CapabilityReasonCode, null>, Tone> = {
  can_bus_not_reporting: 'info',
  optional_peripherals_not_reporting: 'neutral',
  no_telemetry_in_window: 'warning',
}

function Callout({
  icon: Icon,
  tone,
  title,
  children,
  className,
}: {
  icon: LucideIcon
  tone: Tone
  title: string
  children?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex gap-3 rounded-lg border p-3', TONES[tone], className)}>
      <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-semibold">{title}</p>
        <div className="space-y-1 text-sm leading-relaxed text-foreground-muted">{children}</div>
      </div>
    </div>
  )
}

/**
 * The CAN sentence, on its own, for the places a CAN-only reading would have been.
 *
 * Takes the profile rather than a boolean so the program number can be shown; a
 * caller with no profile yet gets the sentence without it, which is still the right
 * sentence.
 *
 * Phase 4 splits the sentence in two. A bus that has NEVER reported is a program
 * number or an adapter — something that was never configured. A bus that reported
 * until a date and then stopped is a harness, a fuse or a battery disconnect on a
 * vehicle that was working. Same blank dial, two different phone calls, so the date
 * is part of the message rather than a detail buried in a profile.
 */
export function CanUnavailableNotice({
  profile,
  className,
}: {
  profile?: CapabilityProfile
  className?: string
}) {
  const { t } = useTranslation()
  const formatters = useFormatters()
  const verdict = canBusVerdict(profile)
  const stopped = verdict.status === 'stopped_reporting'

  return (
    <Callout
      icon={stopped ? PlugZap : CircuitBoard}
      tone={stopped ? 'warning' : 'info'}
      title={t(
        stopped
          ? 'vehicles:capability.canBusStopped.title'
          : 'vehicles:capability.canBusNotReporting.title',
      )}
      className={className}
    >
      <p>
        {t(
          stopped
            ? 'vehicles:capability.canBusStopped.description'
            : 'vehicles:capability.canBusNotReporting.description',
        )}
      </p>
      {stopped ? (
        <p className="text-xs font-medium">
          {verdict.lastSeenAt === null
            ? t('vehicles:capability.canBusStopped.dateUnknown')
            : t('vehicles:capability.canBusStopped.since', {
                when: formatters.dateTime(verdict.lastSeenAt),
              })}
        </p>
      ) : null}
      <p className="text-xs">
        {/* Rule 4: the program number IS the explanation, so it travels with the
            message. "Aucune donnée CAN, programme 0" tells an installer exactly what
            to change; the same panel without the number tells them to go and look. */}
        {verdict.programNumber === null
          ? t('vehicles:capability.programNumberUnknown')
          : t('vehicles:capability.programNumber', { number: verdict.programNumber })}
      </p>
    </Callout>
  )
}

const STATUS_TONE: Record<CapabilityKnowledge, 'success' | 'warning' | 'danger' | 'neutral'> = {
  reporting: 'success',
  stopped_reporting: 'warning',
  never_reported: 'danger',
  unknown: 'neutral',
}

/** The badge every capability surface uses, so the three states read the same everywhere. */
export function CapabilityStatusBadge({ status }: { status: CapabilityKnowledge }) {
  const { t } = useTranslation()
  return <Badge tone={STATUS_TONE[status]}>{t(`vehicles:capability.status.${status}`)}</Badge>
}

/**
 * The CAN section of the vehicle detail page.
 *
 * It renders in all four states rather than only when something is wrong. A panel
 * that disappears once the bus works is indistinguishable from a panel that failed to
 * load, and the program number is worth reading on a healthy vehicle too — it is the
 * field somebody will change next time the adapter is swapped.
 */
export function CanCapabilityCard({
  profile,
  isLoading = false,
  className,
}: {
  profile?: CapabilityProfile
  isLoading?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const formatters = useFormatters()
  const verdict = canBusVerdict(profile)

  return (
    <Card className={className}>
      <CardHeader
        title={t('vehicles:capability.can.title')}
        description={t('vehicles:capability.can.subtitle')}
        action={<CapabilityStatusBadge status={verdict.status} />}
      />
      <CardContent className="space-y-3">
        {verdict.status === 'reporting' ? (
          <Callout
            icon={CircuitBoard}
            tone="neutral"
            title={t('vehicles:capability.canBusReporting.title')}
          >
            <p>
              {t('vehicles:capability.canBusReporting.description', {
                channels: verdict.reportingChannels,
              })}
            </p>
          </Callout>
        ) : verdict.status === 'unknown' && isLoading ? (
          <p className="text-sm text-foreground-subtle">{t('common:metric.pendingTelemetry')}</p>
        ) : (
          <CanUnavailableNotice profile={profile} />
        )}

        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
          <Fact
            label={t('vehicles:capability.programNumberLabel')}
            value={
              verdict.programNumber === null
                ? t('vehicles:capability.programNumberNever')
                : String(verdict.programNumber)
            }
            /* 0 is a diagnosis, not an absence: the Configurator slot was left empty
               and that is precisely what an installer needs to be told. */
            note={
              verdict.programNumber === 0 ? t('vehicles:capability.programNumberUnset') : undefined
            }
            mono={verdict.programNumber !== null}
          />
          <Fact
            label={t('vehicles:capability.lastCanAt')}
            value={
              verdict.lastSeenAt === null
                ? t('vehicles:capability.lastCanNever')
                : formatters.dateTime(verdict.lastSeenAt)
            }
          />
          <Fact
            label={t('vehicles:capability.canChannels')}
            value={
              profile === undefined
                ? t('common:metric.pendingTelemetry')
                : t('vehicles:capability.canChannelsValue', {
                    reporting: verdict.reportingChannels,
                  })
            }
          />
        </dl>
      </CardContent>
    </Card>
  )
}

function Fact({
  label,
  value,
  note,
  mono = false,
}: {
  label: string
  value: string
  note?: string
  mono?: boolean
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">
        {label}
      </dt>
      <dd className={cn('mt-0.5 text-sm text-foreground', mono && 'font-mono tracking-tight')}>
        {value}
      </dd>
      {note ? <p className="mt-0.5 text-xs text-warning">{note}</p> : null}
    </div>
  )
}

/**
 * The page-level notice. Renders the profile summary when nothing is missing —
 * never nothing at all, because a panel that disappears when everything is fine is
 * indistinguishable from a panel that failed to load.
 */
export function CapabilityNotice({
  profile,
  className,
}: {
  profile: CapabilityProfile
  className?: string
}) {
  const { t } = useTranslation()
  const reason = profile.reasonCode

  if (reason === 'can_bus_not_reporting') {
    return <CanUnavailableNotice profile={profile} className={className} />
  }

  if (reason === null || reason === undefined) {
    return (
      <Callout
        icon={Info}
        tone="neutral"
        title={t('vehicles:capability.reporting.title')}
        className={className}
      >
        <p>
          {t('vehicles:capability.reporting.description', {
            channels: profile.observedChannels,
            days: profile.lookbackDays,
          })}
        </p>
      </Callout>
    )
  }

  // Guarded: a code this build does not know (an API ahead of the web) must degrade
  // to the generic notice, never to an undefined component that takes the page down.
  const key = REASON_KEY[reason] ?? 'canBusNotReporting'
  return (
    <Callout
      icon={REASON_ICON[reason] ?? Info}
      tone={REASON_TONE[reason] ?? 'neutral'}
      title={t(`vehicles:capability.${key}.title`)}
      className={className}
    >
      <p>{t(`vehicles:capability.${key}.description`)}</p>
      {reason === 'no_telemetry_in_window' ? (
        <p className="text-xs">
          {t('vehicles:capability.lookbackDays', { days: profile.lookbackDays })}
        </p>
      ) : null}
    </Callout>
  )
}
